import { Progress } from '@/shared/components/ui/progress';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type MediaUploadProgressProps = {
  fileName: string;
  progress: number; // 0-100
  status: 'uploading' | 'success' | 'error';
  error?: string;
};

/**
 * MediaUploadProgress - Upload progress indicator for S3 uploads
 * 
 * Features:
 * - Visual progress bar (0-100%)
 * - File name display
 * - Status icons (loading, success, error)
 * - Error message display
 * 
 * Usage:
 * - Displayed during file uploads in MediaGalleryModal
 * - Shows real-time upload progress to S3
 * 
 * Integration:
 * - Used with useFileUpload hook progress tracking
 * - Updates in real-time via S3 upload events
 */
export function MediaUploadProgress({
  fileName,
  progress,
  status,
  error,
}: MediaUploadProgressProps) {
  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'uploading':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'uploading':
        return `Uploading... ${progress}%`;
      case 'success':
        return 'Upload complete';
      case 'error':
        return error || 'Upload failed';
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        <div className="flex-shrink-0 mt-0.5">{getStatusIcon()}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* File Name */}
          <p className="text-sm font-medium text-gray-900 truncate">
            {fileName}
          </p>

          {/* Status Text */}
          <p className={`text-xs ${getStatusColor()} mt-1`}>
            {getStatusText()}
          </p>

          {/* Progress Bar */}
          {status === 'uploading' && (
            <div className="mt-2">
              <Progress value={progress} className="h-2" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

